import React, { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../contexts/AppContext";
import { routerNavigation, routers } from "../../commons/constants/routers";
import styles from "./store-management.module.scss";
import { Store } from "../../commons/resources";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";
import StoreCard from "./StoreCard";
import { Col, Row, Spin } from "antd";
import { MODES } from "../../commons/constants/user";
import { STORE_STATUS, STORE_TYPE } from "../../commons/constants/store";
import { STORE_TYPE_TABS } from ".";
import InfiniteScroll from "react-infinite-scroll-component";

interface Props {
  activeTab: string;
  activeTypeTab: string;
}

const NUMBER_OF_ITEMS_IN_ROW = 8;
const StoreOfficial: React.FC<Props> = ({ activeTab, activeTypeTab }) => {
  const { setCurrentStore, user, refreshAll, loading, nextStores, checkHasMoreStore, getStoresData } =
    useContext(AppContext);
  const navigate = useNavigate();
  const storeIdManageByMember = user?.merchantStoreAuthorities?.[0]?.merchantStore?.id;

  const data = useMemo(() => {
    const dataReturn: Store[] = getStoresData(activeTypeTab as STORE_TYPE_TABS);

    return user?.merchant_role?.id === MODES.ADMIN
      ? dataReturn?.filter(item =>
          [STORE_STATUS.LIVE, STORE_STATUS.CLOSED, STORE_STATUS.DRAFT, STORE_STATUS.OFFLINE].includes(item.status)
        )
      : dataReturn?.filter(item => item.id === storeIdManageByMember);
  }, [activeTypeTab, getStoresData, storeIdManageByMember, user?.merchant_role?.id]);

  useEffect(() => {
    if (
      data &&
      data.length > 0 &&
      data.length <= NUMBER_OF_ITEMS_IN_ROW &&
      checkHasMoreStore(activeTypeTab as STORE_TYPE_TABS)
    ) {
      nextStores(activeTypeTab as STORE_TYPE_TABS);
    }
  }, [data]);

  const onSelectStore = (store: Store) => {
    setCurrentStore(store);
    if (store.id) {
      navigate(routerNavigation.STORES.STORE_DASHBOARD(store.id));
    }
  };

  if (user?.merchant_role?.id === MODES.ADMIN) {
    return (
      <div className={styles.storeContainer}>
        {loading && data?.length === 0 ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : data?.length === 0 ? (
          <div className={styles.emptyList}>
            <img className={styles.image} src={Store} alt="store" />
            <div className={styles.title}>You haven’t manage a store yet</div>
            <div className={styles.description}>It’s time to create your store!</div>
            <PrimaryButton className={styles.createStoreBtn} onClick={() => navigate(routers.STORES.CREATE_STORE)}>
              Create store
            </PrimaryButton>
          </div>
        ) : (
          <div
            id="adminStoreList"
            style={{
              height: `calc(100vh - 270px)`,
              overflow: "auto",
            }}
          >
            <InfiniteScroll
              className={styles.storeInfinityScroll}
              dataLength={data.length}
              next={() => nextStores(activeTypeTab as STORE_TYPE_TABS)}
              hasMore={checkHasMoreStore(activeTypeTab as STORE_TYPE_TABS)}
              loader={null}
              scrollableTarget="adminStoreList"
              scrollThreshold={0.9}
            >
              <Row gutter={[24, 12]} style={{ marginTop: 30, marginInline: 0 }}>
                {data.map((store: Store, index: number) => {
                  return (
                    <Col key={store.id} span={6} lg={6} md={12} xs={24}>
                      <StoreCard
                        key={index}
                        storeName={store.name}
                        image={store.logo}
                        onClick={() => onSelectStore(store)}
                        status={store.status}
                        storeId={store.id}
                        refresh={refreshAll}
                        isAutoThresholdEnabled={store.isAutoThresholdEnabled}
                        isTerminalEnabled={store.isTerminalEnabled}
                        storeType={store.type as STORE_TYPE}
                      />
                    </Col>
                  );
                })}
              </Row>
            </InfiniteScroll>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className={styles.storeContainer}>
        {loading && data?.length === 0 ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : (
          <div
            id="userStoreList"
            style={{
              height: `calc(100vh - 270px)`,
              overflow: "auto",
            }}
          >
            <InfiniteScroll
              className={styles.storeInfinityScroll}
              dataLength={data.length}
              next={() => nextStores(activeTypeTab as STORE_TYPE_TABS)}
              hasMore={checkHasMoreStore(activeTypeTab as STORE_TYPE_TABS)}
              loader={null}
              scrollableTarget="userStoreList"
              scrollThreshold={0.9}
            >
              <Row gutter={[24, 12]} style={{ marginTop: 30, marginInline: 0 }}>
                {data.map((store: Store, index: number) => {
                  return (
                    <Col key={store.id} span={6} lg={6} md={12} xs={24}>
                      <StoreCard
                        key={index}
                        storeName={store.name}
                        image={store.logo}
                        onClick={() => onSelectStore(store)}
                        status={store.status}
                        storeId={store.id}
                        refresh={refreshAll}
                        storeIdManageByMember={storeIdManageByMember}
                        isAutoThresholdEnabled={store.isAutoThresholdEnabled}
                        isTerminalEnabled={store.isTerminalEnabled}
                        storeType={store.type as STORE_TYPE}
                      />
                    </Col>
                  );
                })}
              </Row>
            </InfiniteScroll>
          </div>
        )}
      </div>
    );
  }
};

export default StoreOfficial;
